// NetBird overlay (1:1) editor. Full fidelity: render-critical fields
// (gateway_host + route_cidrs) up top; groups/enrollment in a secondary
// section (the request render derives group names from the boundary slug, so
// stored groups are optional overrides). setup_key_secret_ref must be a
// field-qualified vault:<path>#<field> ref (C.3b).

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { netbird } from "../../api/endpoints/siteAccess";
import { qk } from "../../api/queryKeys";
import { ApiError } from "../../api/errors";
import {
  ENROLLMENT_METHODS,
  type EnrollmentMethod,
  type NetbirdConfig,
  type NetbirdIn,
} from "../../api/types/siteAccess";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { SelectField } from "../../components/forms/SelectField";
import { StringListField } from "../../components/forms/StringListField";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { EmptyState } from "../../components/ui/EmptyState";
import { parseVendorExtras, vaultFieldRefError, ipAddressError } from "../../lib/fieldValidators";

const DEFAULTS: NetbirdIn = {
  management_url: "", account_group: "", site_group: "", gateway_group: "", admin_group: "",
  gateway_peer_name: "", gateway_host: "", gateway_lan_ip: "", route_cidrs: [],
  enrollment_method: null, setup_key_secret_ref: "", vendor_extras: {}, notes: [],
};

export function NetbirdEditor({ siteSlug, canWrite }: { siteSlug: string; canWrite: boolean }) {
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    retry: false,
    queryKey: qk.siteNetbird(siteSlug),
    queryFn: () => netbird.get(siteSlug).then((r) => r.data),
  });

  if (q.isPending) return <p style={{ color: "var(--text-dim)" }}>Loading NetBird…</p>;
  const is404 = q.error instanceof ApiError && q.error.status === 404;
  if (q.error && !is404) return <ErrorBanner error={q.error} title="Failed to load NetBird" />;

  if (q.data) {
    return <NetbirdForm key={`existing-${siteSlug}`} siteSlug={siteSlug} canWrite={canWrite} initial={toIn(q.data)} existing />;
  }
  if (creating) {
    return <NetbirdForm key={`new-${siteSlug}`} siteSlug={siteSlug} canWrite={canWrite} initial={DEFAULTS} onCancel={() => setCreating(false)} />;
  }
  return (
    <EmptyState
      title="No NetBird overlay configured"
      description="Remote-access overlay: gateway peer + the site subnets it routes."
      action={canWrite ? <button type="button" style={primaryBtn} onClick={() => setCreating(true)}>Configure NetBird</button> : null}
    />
  );
}

interface NbDraft {
  management_url: string; account_group: string; site_group: string; gateway_group: string;
  admin_group: string; gateway_peer_name: string; gateway_host: string; gateway_lan_ip: string;
  route_cidrs: string[]; enrollment_method: EnrollmentMethod | ""; setup_key_secret_ref: string;
  vendor_extras_json: string; notes: string[];
}

const ENROLLMENT_OPTIONS = [{ value: "", label: "— none —" }, ...ENROLLMENT_METHODS];

function NetbirdForm({
  siteSlug, canWrite, initial, existing, onCancel,
}: {
  siteSlug: string; canWrite: boolean; initial: NetbirdIn; existing?: boolean; onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<NbDraft>(() => toDraft(initial));
  const set = <K extends keyof NbDraft>(k: K, v: NbDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: (body: NetbirdIn) => netbird.put(siteSlug, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.siteNetbird(siteSlug) }); onCancel?.(); },
  });
  const del = useMutation({
    mutationFn: () => netbird.remove(siteSlug),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.siteNetbird(siteSlug) }),
  });

  const vendor = parseVendorExtras(draft.vendor_extras_json);
  const secretErr = vaultFieldRefError(draft.setup_key_secret_ref);
  const lanErr = ipAddressError(draft.gateway_lan_ip);
  const isValid =
    draft.gateway_host.trim() !== "" &&
    draft.route_cidrs.length > 0 &&
    vendor.ok && secretErr === null && lanErr === null;
  const pending = save.isPending || del.isPending;
  const disabled = !canWrite || pending;

  return (
    <FormShell
      submitLabel={existing ? "Save NetBird" : "Create NetBird"}
      onSubmit={(e) => { e.preventDefault(); if (!isValid || disabled) return; save.mutate(fromDraft(draft, vendor.ok ? vendor.value : {})); }}
      onCancel={onCancel}
      pending={pending}
      submitDisabled={!canWrite || !isValid}
      error={save.error ?? del.error}
      errorTitle="NetBird save failed"
      footer={existing && canWrite ? (
        <div>
          <button type="button" style={dangerBtn} disabled={pending}
            onClick={() => { if (window.confirm(`Delete NetBird overlay for ${siteSlug}?`)) del.mutate(); }}>
            Delete NetBird overlay
          </button>
        </div>
      ) : undefined}
    >
      {/* Primary — render-critical */}
      <TextField label="Gateway host" value={draft.gateway_host} onChange={(v) => set("gateway_host", v)} required
        placeholder="svc-fatima-01" helper="The edge host running the NetBird gateway peer." disabled={disabled} />
      <StringListField label="Route CIDRs" value={draft.route_cidrs} onChange={(v) => set("route_cidrs", v)}
        placeholder={"10.129.0.0/19"} helper="Site subnets advertised over the overlay. One per line; at least one." disabled={disabled} />
      <TextField label="Gateway peer name" value={draft.gateway_peer_name} onChange={(v) => set("gateway_peer_name", v)}
        placeholder="svc-1" disabled={disabled} />
      <TextField label="Gateway LAN IP" value={draft.gateway_lan_ip} onChange={(v) => set("gateway_lan_ip", v)}
        placeholder="10.129.0.1" error={lanErr} disabled={disabled} />

      {/* Secondary — groups & enrollment (optional) */}
      <details>
        <summary style={{ cursor: "pointer", fontSize: "0.8rem", color: "var(--text-dim)" }}>
          Groups &amp; enrollment (optional — derived at render)
        </summary>
        <div style={{ display: "grid", gap: 14, marginTop: 12 }}>
          <p style={{ margin: 0, fontSize: "0.75rem", color: "var(--text-dim)" }}>
            Request rendering derives group names from the boundary/tenant slug; stored values here are optional intent overrides.
          </p>
          <TextField label="Account group" value={draft.account_group} onChange={(v) => set("account_group", v)} disabled={disabled} />
          <TextField label="Site group" value={draft.site_group} onChange={(v) => set("site_group", v)} disabled={disabled} />
          <TextField label="Gateway group" value={draft.gateway_group} onChange={(v) => set("gateway_group", v)} disabled={disabled} />
          <TextField label="Admin group" value={draft.admin_group} onChange={(v) => set("admin_group", v)} disabled={disabled} />
          <TextField label="Management URL" value={draft.management_url} onChange={(v) => set("management_url", v)} disabled={disabled} />
          <SelectField label="Enrollment method" value={draft.enrollment_method}
            onChange={(v) => set("enrollment_method", v as EnrollmentMethod | "")} options={ENROLLMENT_OPTIONS} disabled={disabled} />
          <TextField label="Setup key secret ref" value={draft.setup_key_secret_ref}
            onChange={(v) => set("setup_key_secret_ref", v)}
            placeholder="vault:tenants/<slug>/netbird/setup_key#key"
            helper="A field-qualified vault: reference — never a plaintext key." error={secretErr} disabled={disabled} />
        </div>
      </details>

      <StringListField label="Notes" value={draft.notes} onChange={(v) => set("notes", v)} helper="One per line." disabled={disabled} />
      <details>
        <summary style={{ cursor: "pointer", fontSize: "0.8rem", color: "var(--text-dim)" }}>Vendor extras (advanced, JSON)</summary>
        <textarea value={draft.vendor_extras_json} disabled={disabled} rows={5}
          onChange={(e) => set("vendor_extras_json", e.target.value)} placeholder={"{}"}
          style={{ width: "100%", marginTop: 8, padding: "8px 10px", background: "var(--surface-2)",
            border: `1px solid ${vendor.ok ? "var(--border)" : "var(--crit)"}`, borderRadius: 4,
            color: "var(--text)", fontFamily: "monospace", fontSize: "0.85rem", resize: "vertical" }} />
        {!vendor.ok && <span style={{ fontSize: "0.75rem", color: "var(--crit)" }}>{vendor.error}</span>}
      </details>
    </FormShell>
  );
}

function toIn(n: NetbirdConfig): NetbirdIn {
  const { site_id: _site_id, ...rest } = n;
  return rest;
}
function toDraft(n: NetbirdIn): NbDraft {
  const hasExtras = Object.keys(n.vendor_extras ?? {}).length > 0;
  return {
    management_url: n.management_url ?? "", account_group: n.account_group ?? "", site_group: n.site_group ?? "",
    gateway_group: n.gateway_group ?? "", admin_group: n.admin_group ?? "", gateway_peer_name: n.gateway_peer_name ?? "",
    gateway_host: n.gateway_host ?? "", gateway_lan_ip: n.gateway_lan_ip ?? "", route_cidrs: n.route_cidrs ?? [],
    enrollment_method: n.enrollment_method ?? "", setup_key_secret_ref: n.setup_key_secret_ref ?? "",
    vendor_extras_json: hasExtras ? JSON.stringify(n.vendor_extras, null, 2) : "", notes: n.notes ?? [],
  };
}
function fromDraft(d: NbDraft, vendorExtras: Record<string, unknown>): NetbirdIn {
  const orNull = (s: string) => (s.trim() === "" ? null : s.trim());
  return {
    management_url: orNull(d.management_url), account_group: orNull(d.account_group), site_group: orNull(d.site_group),
    gateway_group: orNull(d.gateway_group), admin_group: orNull(d.admin_group), gateway_peer_name: orNull(d.gateway_peer_name),
    gateway_host: orNull(d.gateway_host), gateway_lan_ip: orNull(d.gateway_lan_ip), route_cidrs: d.route_cidrs,
    enrollment_method: d.enrollment_method === "" ? null : d.enrollment_method,
    setup_key_secret_ref: orNull(d.setup_key_secret_ref), vendor_extras: vendorExtras, notes: d.notes,
  };
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--bg)", border: "none", padding: "8px 16px",
  borderRadius: 4, fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
};
const dangerBtn: React.CSSProperties = {
  background: "transparent", color: "var(--crit)", border: "1px solid var(--crit)",
  padding: "6px 12px", borderRadius: 4, fontSize: "0.85rem", cursor: "pointer",
};
